import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { TransactionDto } from "@/features/wallet/api/dto";

/**
 * Confirme la comptabilisation d'une transaction
 * (`POST /Transactions/{id}/confirm-ledger`) — étape 3 du flux §5.3 :
 * libère le blocage, met à jour les soldes, passe la transaction en
 * COMPLETED. Normalement déclenché par Kafka (`ledger.journal.posted`) ;
 * aucun consommateur n'étant câblé, cet appel est fait explicitement par
 * le back-office (rôle flashpay-service / flashpay-admin /
 * flashpay-superadmin).
 */
export const useConfirmLedger = (transactionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "confirm-ledger", transactionId],
    mutationFn: () =>
      mutateData({
        url: walletEndpoints.transactions.confirmLedger(transactionId),
        method: "POST",
      }) as Promise<TransactionDto>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(transactionId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useConfirmLedger failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
